<script>
  import Button from "lib/components/Button.svelte";
  import Modal from "lib/components/Modal.svelte";
  import Header from "lib/components/Header.svelte";
  import { SectionWrapper } from "paxray-components-svelte";
  import FrameworkAssistantChat from "lib/components/paxbot/FrameworkAssistantChat.svelte";
  import FrameworkAssistantProgress from "lib/components/paxbot/FrameworkAssistantProgress.svelte";
  import FrameworkGrowthPanel from "lib/components/paxbot/FrameworkGrowthPanel.svelte";
  import TeamSurveyPanel from "./damprojects-frontend/components/TeamSurveyPanel.svelte";
  import { onDestroy, onMount } from "svelte";
  import { ROUTES } from "@utils/routes.js";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";

  // Aus Configure Framework geoeffnet (#project=<id>): Wege zurueck ins Projekt, etwa zur Team-Umfrage. Bewusst im
  // Anker statt als ?idproject - mit ?idproject bleibt die Projektseite in App.svelte zusaetzlich eingehaengt.
  const idproject = new URLSearchParams(window.location.hash.slice(1)).get("project") || "";
  let projectName = "";

  // Ohne Projekt (Home, Company Settings) immer nach dem Department fragen: einen Entwurf ohne Inhalt verwerfen,
  // sonst waere das Department eines frueheren Projekteinstiegs vorausgewaehlt. Entwuerfe mit Inhalt und
  // Uebergaben aus der Team-Umfrage (autoAnalyze) bleiben erhalten.
  if (!idproject) {
    try {
      const draftKey = "paxray.framework-assistant.draft"; // wie draftStorageKey in FrameworkAssistantChat.svelte
      const draft = JSON.parse(localStorage.getItem(draftKey) || "null");
      const hasContent = draft && (
        draft.autoAnalyze ||
        draft.frameworkCreated ||
        draft.frameworkDraft?.length ||
        draft.selectedL1Processes?.length ||
        draft.taskMiningAssessments?.length ||
        draft.workDescriptionDraft?.trim() ||
        draft.message?.trim()
      );
      if (draft && !hasContent) localStorage.removeItem(draftKey);
    } catch {
      // Ohne Browserspeicher gibt es keinen Entwurf
    }
  }

  async function loadProjectHeader() {
    if (!idproject) return;
    try {
      const response = await fetchWithSession(`/api/damprojects/projects/${encodeURIComponent(idproject)}`);
      if (!response.ok) return;
      const project = await response.json();
      projectName = project.name || "";
    } catch (error) {
      console.error("Could not load the project name for the Framework Assistant:", error);
    }
  }

  // Bewusst zur Tabelle: ohne view=table fuehrte Configure Framework bei unfertigem Entwurf gleich wieder hierher
  function openFrameworkTable() {
    window.open(`${ROUTES.dam.processPresets}?idproject=${encodeURIComponent(idproject)}&view=table`, "_blank", "noopener,noreferrer");
  }

  // Team-Umfragen des Projekts: Stand fuer den Streifen rechts, Verwaltung als Fenster ueber dem Chat
  const SURVEY_REFRESH_MS = 30000;
  let surveys = [];
  let surveysOpen = false;
  let surveyStart = null;
  let projectDepartments = [];
  let projectDepartmentMatched = false;
  let projectTeams = [];
  let selectedTeams = []; // im Team-Schritt gewaehlt - die Team-Umfrage startet mit dem ersten ohne Umfrage
  let surveyTimer;

  async function loadSurveys() {
    if (!idproject) return;
    try {
      const response = await fetchWithSession(`/api/damprojects/team-surveys?idproject=${idproject}`);
      if (!response.ok) return;
      const result = await response.json();
      surveys = result.surveys;
      projectDepartments = result.departments || [];
      projectTeams = result.teams || [];
      matchProjectDepartment(projectDepartments);
    } catch {
      // Der Streifen zeigt dann den letzten Stand
    }
  }

  // Beim Einstieg die Projektabteilung uebernehmen; bei genau einer Abteilung die erneute Auswahl ueberspringen.
  // Ein Entwurf mit Inhalt oder ein spaeterer Schritt bleibt unangetastet.
  function matchProjectDepartment(departments) {
    try {
      if (!departments.length || projectDepartmentMatched) return;
      projectDepartmentMatched = true;
      const draftKey = "paxray.framework-assistant.draft"; // wie draftStorageKey in FrameworkAssistantChat.svelte
      const draft = JSON.parse(localStorage.getItem(draftKey) || "null");
      if (draft?.frameworkDraft?.length) return;
      // Vorerst: Department immer aus dem Projekt uebernehmen und direkt zur Wahl des Startwegs (Schritt 7) -
      // keine Department-Abfrage, kein Team-Schritt. Alte Logik bleibt auskommentiert erhalten.
      // const single = departments.length === 1;
      const matches = (draft?.selectedAreas || []).some((area) => departments.includes(area));
      // if (matches && (!single || draft.step !== 1)) return;
      if (matches && draft.step !== 1) return;
      // const nextDraft = matches
      //   ? { ...draft, step: draft.generationMode && draft.generationMode !== "bottom_up" ? 2 : 8, message: "" }
      //   : { step: single ? 8 : 1, generationMode: "bottom_up", started: true, selectedAreas: single ? departments : [] };
      const nextDraft = matches
        ? { ...draft, step: draft.generationMode && draft.generationMode !== "bottom_up" ? 2 : 7, message: "" }
        : { step: 7, generationMode: "bottom_up", started: true, selectedAreas: departments };
      localStorage.setItem(draftKey, JSON.stringify(nextDraft));
      assistantSession += 1;
    } catch {
      // Ohne Browserspeicher bleibt der Assistent, wie er ist
    }
  }

  function openSurveys(mode = "", transferId = "") {
    const team = selectedTeams.find((item) => !surveys.some((survey) => survey.team_id === item.id)) || null;
    surveyStart = mode || transferId ? { mode, transfer: transferId, team } : null;
    surveysOpen = true;
  }

  function closeSurveys() {
    surveysOpen = false;
    loadSurveys();
  }

  // Transfer ohne vorhandenes Geruest: der Entwurf liegt im Browserspeicher - Chat neu starten, er analysiert ihn sofort
  function surveyDraftReady() {
    surveysOpen = false;
    assistantSession += 1;
  }

  onMount(() => {
    loadProjectHeader();
    loadSurveys();
    if (idproject) surveyTimer = setInterval(loadSurveys, SURVEY_REFRESH_MS);
  });
  onDestroy(() => clearInterval(surveyTimer));

  let assistant;
  let assistantSession = 0;
  let assistantBusy = false;
  let restartModalOpen = false;
  let restartError = "";
  let assistantStarted = true;
  let progressStep = 0;
  // Im Projekt kommt das Department aus dem Projekt - Schritt "Department" nicht anzeigen, Nummerierung verschiebt sich
  $: progressOffset = idproject ? 1 : 0;
  $: progressSteps = idproject
    ? ["Describe work", "Review", "Saved"]
    : ["Department", "Describe work", "Review", "Saved"];
  let frameworkCreated = false;
  // Streifen rechts: Abteilung und Vorschlag aus dem Chat; nach jedem Speichern das Vorhandene neu laden
  let selectedAreas = [];
  let frameworkDraft = [];
  let savedVersion = 0;
  let wasCreated = false;
  $: if (frameworkCreated !== wasCreated) {
    wasCreated = frameworkCreated;
    if (frameworkCreated) savedVersion += 1;
  }

  // Abteilung im Streifen gewechselt: der Chat macht fuer sie beim Team-Schritt weiter, die Teams bleiben
  function switchDepartment(name) {
    if (!name || name === selectedAreas[0]) return;
    try {
      // Team-Schritt vorerst deaktiviert - direkt zur Wahl des Startwegs (vorher step: 8)
      localStorage.setItem("paxray.framework-assistant.draft", JSON.stringify({ step: 7, generationMode: "bottom_up", started: true, selectedAreas: [name], selectedTeams }));
    } catch {
      // Ohne Browserspeicher startet der Chat nur mit der Abteilung
    }
    selectedAreas = [name];
    progressStep = 0;
    frameworkCreated = false;
    assistantSession += 1;
  }

  function restartAssistant() {
    if (assistantBusy) return;
    try {
      if (!assistant?.clearDraft()) return;
      assistantStarted = true;
      progressStep = 0;
      frameworkCreated = false;
      assistantSession += 1;
      restartModalOpen = false;
      restartError = "";
    } catch {
      restartError = "Could not clear the saved draft. Please try again.";
    }
  }
</script>

<Header
  section=""
  area={idproject ? "Setup" : ""}
  projectIdOverride={idproject}
  projectNameOverride={projectName}
  breadcrumb="Framework Assistant"
  showTooltip={false}
>
  {#if assistantStarted}
    <div class="framework-header-progress">
      <FrameworkAssistantProgress
        steps={progressSteps}
        currentStep={Math.max(0, progressStep - progressOffset)}
        completed={frameworkCreated}
        navigableSteps={frameworkCreated ? [] : [0, 1].filter((index) => index < progressStep).map((index) => index - progressOffset).filter((index) => index >= 0)}
        disabled={assistantBusy}
        on:navigate={(event) => (event.detail + progressOffset === 1 ? assistant?.navigateToWork() : assistant?.navigateToDepartment())}
      />
    </div>
    <Button
      text="Restart"
      variant="secondary"
      size="sm"
      disabled={assistantBusy}
      on:click={() => {
        restartError = "";
        restartModalOpen = true;
      }}
    />
  {/if}
</Header>

<SectionWrapper
  fullscreen={true}
  seamless={true}
  seamlessPadding={false}
>
  <div slot="main" class="framework-assistant">
    <div class="framework-assistant-chat">
      {#key assistantSession}
        <FrameworkAssistantChat
          bind:this={assistant}
          bind:busy={assistantBusy}
          moveToBottomOnSubmit={true}
          bind:started={assistantStarted}
          bind:progressStep
          bind:frameworkCreated
          bind:selectedAreas
          bind:frameworkDraft
          projectId={idproject}
          {projectDepartments}
          bind:projectTeams
          bind:selectedTeams
          busyTeamIds={surveys.map((survey) => survey.team_id)}
          on:teamSurvey={() => openSurveys("both")}
          on:surveyCreated={loadSurveys}
          on:teamsCreated={loadSurveys}
          on:frameworkChanged={() => (savedVersion += 1)}
        />
      {/key}
    </div>
    <FrameworkGrowthPanel areas={selectedAreas} draft={frameworkCreated ? [] : frameworkDraft} {savedVersion} {surveys} inProject={Boolean(idproject)}
      projectId={idproject}
      departmentOptions={projectDepartments} teams={projectTeams} {selectedTeams}
      on:department={(event) => switchDepartment(event.detail)} on:teams={(event) => (selectedTeams = event.detail)}
      on:teamsCreated={loadSurveys}
      on:manageSurveys={(event) => openSurveys("", event.detail || "")} on:newSurvey={(event) => openSurveys(event.detail)} on:configure={openFrameworkTable} />
  </div>
</SectionWrapper>

{#if idproject}
  <Modal buttonSize="sm" bind:open={surveysOpen} modalHeading="Collect tasks with the team"
    tooltipText="Everyone in the team lists their daily tasks via a link. The survey is anonymous and takes about 15 minutes. Pax AI merges identical tasks, the team chooses what to look at, and Transfer turns the result into the framework."
    autowidth modalClass="team-surveys-modal" actions={false} hideFooter on:close={closeSurveys}>
    {#if surveysOpen}
      <TeamSurveyPanel {idproject} inAssistant startWith={surveyStart}
        on:assistantDraft={surveyDraftReady} on:framework={() => { savedVersion += 1; loadSurveys(); }} on:scope={loadSurveys} />
    {/if}
  </Modal>
{/if}

<Modal buttonSize="sm"
  bind:open={restartModalOpen}
  modalHeading="Restart Framework Assistant?"
  modalClass="framework-restart-modal"
  autowidth
  primaryButtonText="Discard draft & restart"
  primaryButtonVariant="secondary"
  secondaryButtonText="Keep editing"
  actions
  primaryButtonValidations={assistantBusy}
  primaryButtonAction={restartAssistant}
>
  <p>This discards the current draft, edits, and attachments and returns you to the start. Frameworks and departments already created will be kept.</p>
  {#if restartError}<p class="restart-error" role="alert">{restartError}</p>{/if}
</Modal>

<style>
  :global(.custom-modal.framework-restart-modal .bx--modal-container) {
    width: min(36rem, calc(100vw - 2rem)) !important;
    max-width: calc(100vw - 2rem) !important;
  }

  :global(.custom-modal.framework-restart-modal .actions-buttons-container) {
    flex-wrap: wrap;
  }

  :global(.custom-modal.framework-restart-modal .buttons-container) {
    margin-top: 1rem;
  }

  :global(.custom-modal.framework-restart-modal .bx--modal-scroll-content > .modal_wrapper) {
    padding-bottom: 0.5rem !important;
  }

  .restart-error {
    margin-top: .75rem;
    color: var(--pax-danger);
  }

  /* Die fest positionierten Teile des Chats liegen sonst ueber dem Fenster */
  :global(.custom-modal.team-surveys-modal) {
    z-index: 100000;
  }

  :global(.custom-modal.team-surveys-modal .bx--modal-container) {
    width: min(56rem, calc(100vw - 2rem)) !important;
    max-width: calc(100vw - 2rem) !important;
    max-height: calc(100vh - 2rem);
  }

  :global(.custom-modal.team-surveys-modal .bx--modal-content) {
    padding: 0 1rem .5rem;
  }

  :global(.custom-modal.team-surveys-modal .bx--modal-scroll-content > .modal_wrapper) {
    padding-bottom: 0 !important;
  }

  .framework-header-progress {
    width: min(38rem, calc(100vw - var(--app-side-nav-width) - 21rem));
    min-width: 4rem;
    margin-right: .75rem;
  }

  .framework-assistant {
    display: flex;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
  }

  .framework-assistant-chat {
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
  }

  /* Zu schmal fuer Chat und Streifen: der Chat hat Vorrang */
  @media (max-width: 1100px) {
    .framework-assistant :global(.growth-panel) {
      display: none;
    }
  }

</style>
